import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, ExternalLink, Play } from "lucide-react";
import { useContent, useSettings, fileUrl, api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Lightbox, Reveal } from "@/components/site/common";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useSeo } from "@/lib/seo";

const TABS = [["all", "All"], ["news", "News"], ["press", "Press Coverage"], ["events", "Events & Exhibitions"], ["videos", "Videos"], ["gallery", "Gallery"]];
const ytId = (u = "") => (u.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/) || [])[1];

function MediaCard({ m, onOpen }) {
  const inner = (
    <>
      <div className="img-frame aspect-[16/10] border border-[var(--stone)] relative">
        <img src={fileUrl(m.image)} alt="" loading="lazy" className="w-full h-full object-cover" />
        {m.type === "videos" && <span className="absolute inset-0 flex items-center justify-center"><span className="w-14 h-14 rounded-full bg-[rgba(255,253,249,0.9)] flex items-center justify-center"><Play size={20} /></span></span>}
      </div>
      <div className="flex items-center gap-3 mt-4 text-xs text-[var(--steel)] uppercase tracking-widest"><span className="chip !text-[10px]">{TABS.find((t) => t[0] === m.type)?.[1]}</span>{m.outlet} · {m.date}</div>
      <h3 className="text-2xl mt-2 group-hover:text-[var(--brass)] transition-colors">{m.title}</h3>
      <p className="text-sm text-[var(--graphite)] mt-2">{m.excerpt}</p>
      <span className="text-sm text-[var(--brass)] mt-3 inline-flex items-center gap-1">{m.link ? <>View on {m.outlet} <ExternalLink size={13} /></> : m.type === "videos" ? "Watch" : m.type === "gallery" ? "View" : "Read more →"}</span>
    </>
  );
  if (m.link && !["videos", "gallery"].includes(m.type)) return <a href={m.link} target="_blank" rel="noreferrer" data-testid={`media-card-${m.slug}`} className="group block">{inner}</a>;
  if (["videos", "gallery"].includes(m.type)) return <button data-testid={`media-card-${m.slug}`} onClick={() => onOpen(m)} className="group block text-left">{inner}</button>;
  return <Link to={`/media/${m.slug}`} data-testid={`media-card-${m.slug}`} className="group block">{inner}</Link>;
}

export default function Media() {
  const { data = [] } = useContent("media");
  const { data: s } = useSettings();
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(null);
  useSeo("Media", "News, press coverage, exhibitions, videos and gallery from Tijiya Exports.");
  const featured = data.find((m) => m.featured) || data[0];
  const list = data.filter((m) => (tab === "all" || m.type === tab) && (tab !== "all" || m.id !== featured?.id));
  return (
    <div data-testid="media-page">
      <section className="wrap pt-16 pb-8">
        <p className="eyebrow mb-4">Media</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl">News, coverage & gallery</h1>
        <div className="mt-8 flex flex-wrap gap-2">{TABS.map(([k, l]) => <button key={k} data-testid={`media-tab-${k}`} data-active={tab === k} onClick={() => setTab(k)} className="chip chip-select">{l}</button>)}</div>
      </section>
      {tab === "all" && featured && (
        <section className="wrap pb-14 border-b border-[var(--stone)]">
          <Link to={`/media/${featured.slug}`} data-testid="media-featured" className="group grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 img-frame aspect-[16/9] border border-[var(--stone)]"><img src={fileUrl(featured.image)} alt="" className="w-full h-full object-cover" /></div>
            <div className="lg:col-span-5"><p className="eyebrow">Featured · {featured.outlet} · {featured.date}</p><h2 className="text-4xl mt-3 group-hover:text-[var(--brass)] transition-colors">{featured.title}</h2><p className="mt-4 text-[var(--graphite)]">{featured.excerpt}</p></div>
          </Link>
        </section>
      )}
      <section className="wrap py-14">
        {list.length === 0 ? <p data-testid="media-empty" className="text-sm text-[var(--graphite)]">Nothing here yet.</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">{list.map((m, i) => <Reveal key={m.id} delay={(i % 3) * 80}><MediaCard m={m} onOpen={setOpen} /></Reveal>)}</div>
        )}
      </section>
      <section className="bg-[var(--paper)] border-t border-[var(--stone)]">
        <div className="wrap py-14 flex flex-col md:flex-row justify-between gap-6 md:items-center">
          <div><p className="eyebrow">Press kit</p><p className="serif text-3xl mt-2">Logo pack and company profile</p><p className="text-sm text-[var(--graphite)] mt-2">Media contact: <a className="link" href={`mailto:${s?.media_email || s?.contact?.email}`}>{s?.media_email || s?.contact?.email}</a></p></div>
          {s?.press_kit_url ? <a href={fileUrl(s.press_kit_url)} target="_blank" rel="noreferrer" data-testid="press-kit-download" className="btn btn-outline"><Download size={15} /> Download press kit</a> : <a href="/logo.png" download data-testid="press-kit-download" className="btn btn-outline"><Download size={15} /> Download logo</a>}
        </div>
      </section>
      <Lightbox src={open?.type === "gallery" ? fileUrl(open.image) : null} alt={open?.title} onClose={() => setOpen(null)} />
      <Dialog open={open?.type === "videos"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl bg-[var(--paper)] p-4"><DialogTitle className="sr-only">{open?.title}</DialogTitle>
          {ytId(open?.video) ? <iframe title={open?.title} className="w-full aspect-video" src={`https://www.youtube-nocookie.com/embed/${ytId(open.video)}`} allow="autoplay; encrypted-media" allowFullScreen /> : <p className="p-6 text-sm">Video link not set.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function MediaArticle() {
  const { slug } = useParams();
  const { data: m, isError } = useQuery({ queryKey: ["media", slug], queryFn: () => api.get(`/media/${slug}`).then((r) => r.data) });
  useSeo(m?.title, m?.excerpt, m && { "@context": "https://schema.org", "@type": "NewsArticle", headline: m.title, datePublished: m.date, image: [fileUrl(m.image)], publisher: { "@type": "Organization", name: "Tijiya Exports" } });
  if (isError) return <div className="wrap py-32"><h1 className="text-4xl">Article not found</h1><Link to="/media" className="link">Back to media</Link></div>;
  if (!m) return <div className="wrap py-32 text-sm">Loading…</div>;
  return (
    <article data-testid="media-article" className="wrap py-16 max-w-3xl">
      <Link to="/media" className="text-xs link">← Media</Link>
      <p className="eyebrow mt-8">{m.outlet} · {m.date}</p>
      <h1 className="text-4xl sm:text-5xl mt-4">{m.title}</h1>
      {m.image && <img src={fileUrl(m.image)} alt="" className="w-full aspect-[16/9] object-cover mt-10 border border-[var(--stone)]" />}
      <div className="mt-10 space-y-5 md:text-lg text-[var(--graphite)] whitespace-pre-line">{m.body}</div>
      {m.link && <a href={m.link} target="_blank" rel="noreferrer" className="btn btn-outline mt-10">View on {m.outlet} <ExternalLink size={14} /></a>}
    </article>
  );
}
